import { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const LEFT_BUS_API =
  "https://q1xgsyocc2.execute-api.ap-south-1.amazonaws.com/Prod/LeftBus";

function LeftBuses() {
  const [leftBuses, setLeftBuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const MySwal = withReactContent(Swal);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const today = new Date().toISOString().split("T")[0];

  const fetchLeftBuses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(LEFT_BUS_API, {
        headers: { Authorization: `${localStorage.getItem("token")}` },
      });
      const data =
        response.data?.body?.data || response.data?.data || response.data || [];
      setLeftBuses(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error("Failed to fetch left buses", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeftBuses();
  }, []);

  const handleRecallBus = async (bus) => {
    const vehicleNumber = bus.vehicleNumber || bus.generalInfo?.vehicleNumber;
    const busNumber = bus.busNumber || bus.generalInfo?.busNumber;

    const result = await MySwal.fire({
      title: "Recall Bus?",
      text: `Are you sure you want to recall Bus ${busNumber} (${vehicleNumber})?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#198754",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Recall!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          LEFT_BUS_API,
          { vehicleNumber, busNumber, action: "recall" },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );
        if (response.status === 200) {
          toast.success(`Bus ${busNumber} recalled successfully`, {
            position: "top-right",
          });
          fetchLeftBuses();
        } else {
          toast.error("Recall failed. Please try again.", {
            position: "top-right",
          });
        }
      } catch (error) {
        toast.error("Error occurred. Please try again.", {
          position: "top-right",
        });
      }
    }
  };

  const filteredRecords = leftBuses.filter((bus) => {
    const searchLower = searchTerm.toLowerCase();
    const flatten = (obj) => {
      const vals = [];
      for (const k in obj) {
        const v = obj[k];
        if (v && typeof v === "object") vals.push(...flatten(v));
        else if (typeof v === "string") vals.push(v);
      }
      return vals;
    };
    return flatten(bus).some((val) => val.toLowerCase().includes(searchLower));
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  filteredRecords.sort((a, b) => {
    const numA =
      (a.busNumber || a.generalInfo?.busNumber || "")?.match(/\d+/)?.[0] ?? 0;
    const numB =
      (b.busNumber || b.generalInfo?.busNumber || "")?.match(/\d+/)?.[0] ?? 0;
    return Number(numA) - Number(numB);
  });

  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleExportExcel = () => {
    if (!leftBuses.length) {
      toast.error("No data to export");
      return;
    }
    const sheetData = leftBuses.map((bus) => ({
      "Bus Number": bus.busNumber || bus.generalInfo?.busNumber || "",
      "Vehicle Number":
        bus.vehicleNumber || bus.generalInfo?.vehicleNumber || "",
      "Owner Name": bus.ownerName || bus.generalInfo?.ownerName || "",
      "Total Seats": bus.numberOfSeats || bus.generalInfo?.numberOfSeats || "",
      "GPRS Status": bus.gprsStatus || bus.generalInfo?.gprsStatus || "",
      "CCTV Installed":
        bus.cctvInstalled || bus.generalInfo?.cctvInstalled || "",
    }));
    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Left Buses");
    XLSX.writeFile(workbook, `LeftBuses_${today}.xlsx`);
    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="">
            <div className="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div className="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table className="table mb-0 text-nowrap table-centered">
                      <thead className="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((bus, index) => {
                          const gen = bus.generalInfo || bus;
                          const ins = bus.insurance || {};
                          const formatD = (d) =>
                            d ? new Date(d).toLocaleDateString("en-GB") : "N/A";
                          return (
                            <tr key={index}>
                              <td>{indexOfFirstRecord + index + 1}.</td>
                              <td className="lh-sm py-4 text-wrap">
                                <span className="text-dark">Vehicle No</span>{" "}
                                &mdash; {gen.vehicleNumber}
                                <br />
                                <span className="text-dark">Bus No</span>{" "}
                                &mdash; {gen.busNumber}
                                <br />
                                <span className="text-dark">
                                  Total Seats
                                </span>{" "}
                                &mdash; {gen.numberOfSeats}
                                <br />
                                <span className="text-dark">
                                  Chassis No
                                </span>{" "}
                                &mdash; {gen.chassisNumber}
                                <br />
                                <span className="text-dark">
                                  Engine No
                                </span>{" "}
                                &mdash; {gen.engineNumber}
                                <br />
                                <span className="text-dark">Tax Date</span>{" "}
                                &mdash; {formatD(gen.tasPaidDate)} To{" "}
                                {formatD(gen.tasPaidExpiryDate)}
                                <br />
                                <span className="text-dark">
                                  Fitness Date
                                </span>{" "}
                                &mdash; {formatD(gen.fitenessDate)} To{" "}
                                {formatD(gen.fitnessRenewalDate)}
                                <br />
                                <span className="text-dark">
                                  Pollution Date
                                </span>{" "}
                                &mdash; {formatD(gen.pollutionControlDate)} To{" "}
                                {formatD(gen.pollutionControlExpiryDate)}
                                <br />
                                <span className="text-dark">
                                  GPRS Status
                                </span>{" "}
                                &mdash; {gen.gprsStatus}
                                <br />
                                <span className="text-dark">
                                  CCTV Status
                                </span>{" "}
                                &mdash; {gen.cctvInstalled}
                                <br />
                                <span className="text-dark">
                                  Owner
                                </span> &mdash; {gen.ownerName}
                                {(ins.insurancePolicyNumber ||
                                  bus.insurancePolicyNumber) && (
                                  <>
                                    <hr className="w-25" />
                                    <span className="text-dark">
                                      Insurance Policy No
                                    </span>{" "}
                                    &mdash;{" "}
                                    {ins.insurancePolicyNumber ||
                                      bus.insurancePolicyNumber}
                                    <br />
                                    <span className="text-dark">
                                      Insurance Amount
                                    </span>{" "}
                                    &mdash;{" "}
                                    {ins.insuranceAmount || bus.insuranceAmount}
                                    <br />
                                    <span className="text-dark">
                                      Insurance Date
                                    </span>{" "}
                                    &mdash;{" "}
                                    {formatD(
                                      ins.insuranceDate || bus.insuranceDate,
                                    )}{" "}
                                    To{" "}
                                    {formatD(
                                      ins.insuranceExpiryDate ||
                                        bus.insuranceExpiryDate,
                                    )}
                                  </>
                                )}
                              </td>
                              <td>
                                <div className="d-flex flex-column gap-1 width-fit-content">
                                  <span
                                    onClick={() => handleRecallBus(bus)}
                                    className="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                  >
                                    Recall Bus
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {!loading && currentRecords.length === 0 && (
                          <tr>
                            <td
                              colSpan="3"
                              className="text-center py-5 text-secondary"
                            >
                              No left buses found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div>
                    <div className="mb-3">
                      <div className="bg-white rounded-pill">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Transport Management</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              Left Buses
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}
                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Left Buses
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              {leftBuses.length} Records
                            </span>
                          </p>
                        </div>
                        <span
                          onClick={handleExportExcel}
                          className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer"
                        >
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#leftBusReports"
                            role="button"
                            aria-expanded="false"
                            aria-controls="leftBusReports"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="leftBusReports"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span
                                  onClick={handleExportExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-pill mb-1 me-1"
                                >
                                  All Left Buses
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>
                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);
                            if (currentPage > 3)
                              pageNumbers.push("left-ellipsis");
                            if (currentPage !== 1 && currentPage !== totalPages)
                              pageNumbers.push(currentPage);
                            if (currentPage < totalPages - 2)
                              pageNumbers.push("right-ellipsis");
                            if (totalPages > 1) pageNumbers.push(totalPages);
                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }
                              return (
                                <li
                                  key={index}
                                  className={`page-item ${currentPage === page ? "active" : ""}`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}
                          <li
                            className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default LeftBuses;
